import { AcceptedHtmlElementContent, createHtmlElement } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { Action, ActionParams } from "@cervval.js/common-action/src/action.model";
import { ACTION_SERVICE, ActionService, ProcessOperations } from "@cervval.js/common-action/src/services/action.service";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { ItemPropertyOrFunction, ItemPropertyOrGetSet, getItemPOFValue, getItemPOGSValue, getVOFValue, setItemPOGSValue } from "@cervval.js/common/src/tools/object.tools";
import { DEFAULT_DATE_FORMAT, UnixTimestamp, format, isAfter } from "@cervval.js/common/src/tools/timestamps.tools";
import { anyReallyAny } from "@cervval.js/common/tools";
import { Tooltip } from "../../../../bootstrap";
import { ColumnUid } from "./dayAgenda.types";
import { DayAgendaItemContext } from "./dayAgendaContext";
import { ItemInteraction } from "./dayAgendaEvents";
import { HandleType } from "./dayAgendaInteractions.helper";
import { AgendaItemStyle } from "./dayAgendaOptions";

export const DEFAULT_ITEM_OVERLAP = false;
export const DEFAULT_ITEM_HEIGHT_FACTOR = 1;

export type PositionProperties = {
    pxX: number,
    pxY: number,
    height: number,
    startTs: UnixTimestamp,
    finishTs: UnixTimestamp,
    hasBeenMoved: boolean
}

export interface DayAgendaModel<
    ItemObject,
    ColumnObject
> {

    //#region GETTERS

    /** Getter : Item's agenda cols' uid */
    getItemColumnUids(item: ItemObject): string[];

    /** Getter: Column's agenda day (starting day uts) */
    getColumnDay(column: ColumnObject): UnixTimestamp;

    /** Getter : Item's uid */
    getItemUid(item: ItemObject): string;

    /** Find item by uid, this function allows you to handle item uids that can change  */
    findItemByUid?(items: ItemObject[], uid: string): ItemObject | undefined;

    /** Getter : Item's style with css like properties */
    getItemStyle(item: ItemObject, colItemsDefaultStyle: AgendaItemStyle): AgendaItemStyle;

    /** 
     * Getter : Item's text to be displayed
     * For HTML, use getItemRenderer
     */
    getItemText(item: ItemObject): string;

    /**
     * Getter : Item's renderer to set on agenda.
     */
    getItemRenderer(item: ItemObject): HTMLElement;

    /** Getter : Item's start timestamp */
    getItemStartTs(item: ItemObject): UnixTimestamp;

    /** Getter : Item's finish timestamp */
    getItemFinishTs(item: ItemObject): UnixTimestamp;

    /** Getter : Item's tooltip content */
    getItemTooltipContent(item: ItemObject): AcceptedHtmlElementContent;

    /** Getter : Item's tooltip options. */
    getItemTooltipOptions?(item: ItemObject): Partial<Tooltip.Options>;

    /** Getter : Columns's uid */
    getColumnUid(p: { day?: UnixTimestamp, column?: ColumnObject }): string;

    /** Getter : Column's label to be displayed. */
    getColumnLabel(column: ColumnObject): string;

    /**
     * Getter : Value used to sets the z-order of an item
     * Items with same z-order will stacking in yRank.
     * Overlapping elements with a larger z-index cover those with a smaller one
     * You can use negative or positive values, default itemLayerIndex is 0
     * ! itemLayerIndex isn't style z-index, it will only impact drawing order of items in AgendaColumnView
     * @default 0
     */
    getItemLayerIndex(item: ItemObject): number;

    //#endregion GETTERS

    //#region SETTERS

    /** Setter POGS : Item's start timestamp. Does not call encapsulateInAction */
    setItemStartTs(item: ItemObject, startTs: UnixTimestamp, action: Action<unknown, unknown, anyReallyAny>, interaction?: ItemInteraction): Promise<boolean>;

    /** Setter POGS : Item's finish timestamp. Does not call encapsulateInAction  */
    setItemFinishTs(item: ItemObject, finishTs: UnixTimestamp, action: Action<unknown, unknown, anyReallyAny>, interaction?: ItemInteraction): Promise<boolean>;

    /** Setter POGS : Item's cols' uid */
    setItemColumnUids(item: ItemObject, columnUids: ColumnUid[], action: Action<unknown, unknown, anyReallyAny>): Promise<boolean>;

    /** Create an item (in some case, can create more than one), Does not call encapsulationInAction */
    createItem(col: ColumnObject, startTs: UnixTimestamp, finishTs: UnixTimestamp, label?: string, action?: Action<unknown, unknown, anyReallyAny>): Promise<ItemObject>;

    /** Update an item (in some case, can update more than one), Does not call encapsulationInAction */
    updateItem(item: ItemObject, col: ColumnObject, startTs: UnixTimestamp, finishTs: UnixTimestamp, label?: string, action?: Action<unknown, unknown, anyReallyAny>): Promise<ItemObject>;

    /** Delete an item (in some case, can delete more than one), Does not call encapsulationInAction */
    deleteItem(item: ItemObject | ItemObject[], action: Action<unknown, unknown, anyReallyAny>): Promise<ItemObject | ItemObject[]>;

    /** Resize item (bypass standard resize) */
    resizeItem?(p: {
        itemObject: ItemObject,
        newProperties: PositionProperties,
        handleType: HandleType
    }, action: Action<unknown, unknown, anyReallyAny>): Promise<"cancelled" | "done" | "continue_with_standard_process">;

    /** Move item (bypass standard move) */
    moveItem?(p: {
        itemObject: ItemObject,
        updatesToDo: {
            setItemColUids: boolean,
            setTimestamps: boolean
        },
        itemColumnUids: ColumnUid[],
        newProperties: PositionProperties

    }, action: Action<unknown, unknown, anyReallyAny>): Promise<"cancelled" | "done" | "continue_with_standard_process">;

    /**
     * Callback called after an item was moved
     * Called after :
     * - moveItem
     * - setItemColsUid
     * - setItemStartTs
     * - setItemFinishTs
     */
    afterMoveItemProcess?(p: {
        itemObject: ItemObject,
        itemColumnUids: ColumnUid[],
        action: Action<unknown, unknown, anyReallyAny>
    }): Promise<void>;

    /**
     * Callback called after an item was resized
     * Called after :
     * - resizeItem
     * - setItemStartTs
     * - setItemFinishTs
     */
    afterResizeItemProcess?(p: {
        itemObject: ItemObject,
        action: Action<unknown, unknown, anyReallyAny>
    },): void;

    /** EncapsulateInAction used to update items */
    encapsulateInAction(
        processOperations: ProcessOperations<unknown, unknown, unknown, anyReallyAny>,
        params: ActionParams<unknown, unknown, anyReallyAny>): Promise<anyReallyAny>;

    //#endregion

    //#region CUSTOM

    /**
     * This function is used to compare contexts that have same startTs and finishTs
     * By default, they are compared based on their uid
     * <AgendaItemObject> instead of <ItemObject> because otherwise Typescript generates many errors in our codebase
     */
    compareItemContexts?<AgendaItemObject extends ItemObject>(i1: DayAgendaItemContext<AgendaItemObject>, i2: DayAgendaItemContext<AgendaItemObject>): number;

    //#endregion CUSTOM

    //#region PERMISSIONS

    /** Permission : Can the item change position (be it vertical or horizontal) */
    canMoveItem(item: ItemObject): boolean;

    /** Permission : Can the item's col be changed (moved to another col - vertically moved) */
    canChangeColumn(item: ItemObject, destinationCol: ColumnObject): boolean;

    /** Permission : Can the item's start and/or finish timestamps be changed (horizontally moved) */
    canRescheduleItem(item: ItemObject, newStartTs: UnixTimestamp, newFinishTs: UnixTimestamp): boolean;

    /** Permission : Can the item be dropped into destinationCol (be it vertically or horizontal moved) */
    canDropItemOnColumn(item: ItemObject, newStartTs: UnixTimestamp, newFinishTs: UnixTimestamp, originCol: ColumnObject, destinationCol: ColumnObject): boolean;

    /** Permission : Can the item be resized */
    canResizeItem(item: ItemObject): boolean;

    /** Permission : Is the agenda editable */
    isEditable(): boolean;

    /** Permission : Is the agenda editable on the mouse down timestamp */
    isEditableOnTs?(ts: UnixTimestamp): boolean;

    /** Permission: Allow the item creation on a specified col */
    canCreateItemOnColumn(col: ColumnObject, startTs: UnixTimestamp): boolean;

    /** Permission: Allow an item to be created */
    canCreateItem(col: ColumnObject, startTs: UnixTimestamp, finishTs: UnixTimestamp): boolean;

    /** Permission: Allow an item to be deleted */
    canDeleteItem(item: ItemObject): boolean;

    /** Permission: Allow an item to be selected */
    canSelectItem?(item: ItemObject): boolean;
    //#endregion PERMISSIONS

}

export interface BasicDayAgendaModelParams<
    ItemObject,
    ColumnObject
> {
    columnUid: ItemPropertyOrFunction<ColumnObject, string>;
    columnDay?: ItemPropertyOrFunction<ColumnObject, UnixTimestamp>;
    columnLabel: ItemPropertyOrFunction<ColumnObject, string>;
    itemColumnUids: ItemPropertyOrGetSet<ItemObject, ColumnUid[]>;
    itemUid: ItemPropertyOrFunction<ItemObject, string>;
    itemStartTs: ItemPropertyOrGetSet<ItemObject, UnixTimestamp>;
    itemFinishTs: ItemPropertyOrGetSet<ItemObject, UnixTimestamp>;
    itemTooltip: ItemPropertyOrFunction<ItemObject, AcceptedHtmlElementContent>;
    itemTooltipOptions?: ItemPropertyOrFunction<ItemObject, Partial<Tooltip.Options>>;
    itemText?: ItemPropertyOrFunction<ItemObject, string>;
    itemRenderer?: ItemPropertyOrFunction<ItemObject, HTMLElement>;// TODO remove (duplicate with agenda option)
    isEditable?: boolean | (() => boolean);
    itemLayerIndex?: ItemPropertyOrFunction<ItemObject, number>;
    compareItemContexts?: <AgendaItemObject extends ItemObject>(i1: DayAgendaItemContext<AgendaItemObject>, i2: DayAgendaItemContext<AgendaItemObject>) => number;
}

/** Basic Agenda Model with POF getters */
export class BasicDayAgendaModel<
    ItemObject,
    ColumnObject
> implements DayAgendaModel<ItemObject, ColumnObject> {

    @Injected(ACTION_SERVICE)
    protected _actionService!: ActionService<unknown, unknown>;

    constructor(public params: BasicDayAgendaModelParams<ItemObject, ColumnObject>) {
        this.compareItemContexts = params.compareItemContexts;
    }

    /** Getter POGS : Item's cols' uid */
    public getItemColumnUids(item: ItemObject): string[] {
        return getItemPOGSValue(item, this.params.itemColumnUids)!;
    }

    /** Getter POGS : Item's uid */
    public getItemUid(item: ItemObject): string {
        return getItemPOFValue(item, this.params.itemUid)!;
    }

    /** Getter : Col Item's default style with css like properties */
    public getItemStyle(item: ItemObject, colItemsDefaultStyle: AgendaItemStyle): AgendaItemStyle {
        return colItemsDefaultStyle;
    }

    /** Getter POGS : Item's text */
    public getItemText(item: ItemObject): string {
        if (this.params.itemText) {
            return getItemPOFValue(item, this.params.itemText) ?? "";
        }
        return "";
    }

    /** 
     * Getter POGS : Item's renderer 
     * @deprecated
     * TODO remove getItemRenderer : la fonction itemRender de AgendaColOptions est concue pour cela et a plus d'informations
     */
    public getItemRenderer(item: ItemObject): HTMLElement {
        if (this.params.itemRenderer) {
            return getItemPOFValue(item, this.params.itemRenderer) ?? createHtmlElement("div");
        }
        return createHtmlElement("div");
    }


    /** Getter POGS : Item's start timestamp */
    public getItemStartTs(item: ItemObject): UnixTimestamp {
        return getItemPOGSValue(item, this.params.itemStartTs)!;
    }

    /** Getter POGS : Item's finish timestamp */
    public getItemFinishTs(item: ItemObject): UnixTimestamp {
        return getItemPOGSValue(item, this.params.itemFinishTs)!;
    }

    /** Getter POGS : Item's tooltip content */
    public getItemTooltipContent(item: ItemObject): AcceptedHtmlElementContent {
        return getItemPOFValue(item, this.params.itemTooltip) ?? "";
    }

    /** Getter POGS : Item's tooltip options. */
    public getItemTooltipOptions(item: ItemObject): Partial<Tooltip.Options> {
        if (this.params.itemTooltipOptions) {
            return getItemPOFValue(item, this.params.itemTooltipOptions) ?? {};
        }
        return {};
    }

    /** Getter POGS : Column's uid */
    public getColumnUid(p: { day?: UnixTimestamp, column?: ColumnObject }): string {
        if (p.column) {
            return getItemPOFValue(p.column, this.params.columnUid)!;
        }
        if (p.day != null) {
            return format(p.day, DEFAULT_DATE_FORMAT);
        }
        throw "getColumnUid not implemented yet";
    }

    /** Getter POGS : Column's label */
    public getColumnLabel(col: ColumnObject): string {
        return getItemPOFValue(col, this.params.columnLabel) ?? "";
    }

    /** Getter POGS : Item's cols' uid */
    public getColumnDay(column: ColumnObject): UnixTimestamp {
        if (this.params.columnDay) {
            return getItemPOFValue(column, this.params.columnDay)!;
        }
        throw "getColumnDay not implemented yet";
    }

    /**
     * Getter POGS: Value used to sets the z-order (layer) of an item
     * Items with same z-order will stacking in yRank.
     * Overlapping elements with a larger z-index cover those with a smaller one
     * You can use negative or positive values, default itemLayerIndex is 0
     * ! itemLayerIndex isn't style z-index, it will only impact drawing order of items in AgendaColumnView
     * @default 0
     */
    public getItemLayerIndex(item: ItemObject): number {
        if (this.params.itemLayerIndex != null) {
            return getItemPOFValue(item, this.params.itemLayerIndex) ?? 0;
        }
        return 0;
    }


    /** Permission : Can the item change position (be it vertical or horizontal) */
    public canMoveItem(item: ItemObject): boolean {
        return this.isEditable();
    }

    /** Permission : Can the item be moved to another col (vertically moved) */
    public canChangeColumn(item: ItemObject, destinationCol: ColumnObject): boolean {
        return this.isEditable();
    }

    /** Permission : Can the item be drop into col (be it vertically or horizontal moved) */
    public canDropItemOnColumn(item: ItemObject, newStartTs: UnixTimestamp, newFinishTs: UnixTimestamp, originCol: ColumnObject, destinationCol: ColumnObject): boolean {
        return this.isEditable();
    }

    /** Permission : Can the item's start and/or finish timestamps be changed (horizontally moved) */
    public canRescheduleItem(item: ItemObject, newStartTs: UnixTimestamp, newFinishTs: UnixTimestamp): boolean {
        return this.isEditable();
    }

    /** Permission : Can the item be resized */
    public canResizeItem(item: ItemObject): boolean {
        return this.isEditable();
    }

    /** Setter POGS : Item's start timestamp. Does not call encapsulateInAction */
    public setItemStartTs(item: ItemObject, startTs: UnixTimestamp): Promise<boolean> {
        return Promise.resolve(setItemPOGSValue(item, this.params.itemStartTs, startTs));
    }

    /** Setter POGS : Item's finish timestamp. Does not call encapsulateInAction  */
    public setItemFinishTs(item: ItemObject, finishTs: UnixTimestamp): Promise<boolean> {
        return Promise.resolve(setItemPOGSValue(item, this.params.itemFinishTs, finishTs));
    }

    /** Setter POGS : Item's cols' uid */
    public setItemColumnUids(item: ItemObject, columnUids: ColumnUid[]): Promise<boolean> {
        return Promise.resolve(setItemPOGSValue(item, this.params.itemColumnUids, columnUids) ?? []);
    }

    /** Permission : Is the agenda editable */
    public isEditable(): boolean {
        return getVOFValue(this.params.isEditable) ?? false;
    }

    /** Create an item (in some case, can create more than one), Does not call encapsulationInAction */
    public createItem(column: ColumnObject, startTs: UnixTimestamp, finishTs: UnixTimestamp, label?: string): Promise<ItemObject> {
        return Promise.reject("CreateItem not implemented yet");
    }

    /** Update an item (in some case, can create more than one), Does not call encapsulationInAction */
    public updateItem(item: ItemObject, column: ColumnObject, startTs: UnixTimestamp, finishTs: UnixTimestamp, label: string): Promise<ItemObject> {
        return Promise.reject("UpdateItem not implemented yet");
    }

    /** Permission: Allow the item creation on a specified col */
    public canCreateItemOnColumn(col: ColumnObject, startTs: UnixTimestamp): boolean {
        return this.isEditable();
    }

    /** Permission: Allow an item to be created */
    public canCreateItem(col: ColumnObject, startTs: UnixTimestamp, finishTs: UnixTimestamp): boolean {
        if (!isAfter(finishTs, startTs)) { return false; }
        return this.canCreateItemOnColumn(col, startTs);
    }

    /** Permission: Allow an item to be deleted */
    public canDeleteItem(item: ItemObject): boolean {
        return this.isEditable();

    }

    /** Delete an item (in some case, can delete more than one), Does not call encapsulationInAction */
    public deleteItem(item: ItemObject | ItemObject[]): Promise<ItemObject | ItemObject[]> {
        return Promise.reject("deleteItem method hasn't been implemented");
    }

    /** encapsulateInAction used to update items */
    public encapsulateInAction<AppLogInfos, Result>(
        processOperations: (action: Action<unknown, AppLogInfos, unknown>) => Promise<Result> | Result,
        params: ActionParams<unknown, AppLogInfos, unknown>): Promise<Result> {
        return this._actionService.encapsulateInAction(processOperations, params);
    }

    public compareItemContexts?<AgendaItemObject extends ItemObject>(i1: DayAgendaItemContext<AgendaItemObject>, i2: DayAgendaItemContext<AgendaItemObject>): number;

}